import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  climbPublisherNav,
  EMPTY_PUBLISHER_NAV,
  PublisherHub,
  type PublisherFormDraft,
  type PublisherNavState,
} from '../../publisher/PublisherHub';
import { getAuthorRouteDraft, saveAuthorRouteDraft } from '../api';
import {
  consumeContentOpenedFromHub,
  isContentOpenedFromHub,
  U153_CONTENT_FROM_HUB,
} from '../hubEntryFlags';

const CONTENT_DRAFT_KEY = 'CONTENT';

interface ContentPublisherOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  activeRole?: string;
  /** Called when chrome Close should flush draft before leaving Hub entirely. */
  onFlushDraft?: () => void;
}

export const ContentPublisherOverlay: React.FC<ContentPublisherOverlayProps> = ({
  isOpen,
  onClose,
  onBack,
  activeRole,
}) => {
  const [nav, setNav] = useState<PublisherNavState>(EMPTY_PUBLISHER_NAV);
  const [formDraft, setFormDraft] = useState<PublisherFormDraft>({});
  const draftLoadedRef = useRef(false);
  const navRef = useRef(nav);
  const formRef = useRef(formDraft);
  navRef.current = nav;
  formRef.current = formDraft;

  const persistDraft = useCallback(
    async (nextNav = navRef.current, nextForm = formRef.current) => {
      try {
        await saveAuthorRouteDraft({
          route_key: CONTENT_DRAFT_KEY,
          step:
            nextNav.level === 'form' ? 2 : nextNav.level === 'kinds' ? 1 : 0,
          data: {
            nav: nextNav,
            form: nextForm,
            role: activeRole || null,
          },
        });
      } catch {
        /* keep local */
      }
    },
    [activeRole]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';

    if (!draftLoadedRef.current) {
      draftLoadedRef.current = true;
      getAuthorRouteDraft(CONTENT_DRAFT_KEY)
        .then((draft) => {
          if (!draft?.data) return;
          const savedNav = draft.data.nav as PublisherNavState | undefined;
          const savedForm = draft.data.form as PublisherFormDraft | undefined;
          if (savedNav && typeof savedNav === 'object') {
            setNav({
              level: savedNav.level || 'home',
              direction: savedNav.direction ?? null,
              kind: savedNav.kind ?? null,
            });
          }
          if (savedForm && typeof savedForm === 'object') {
            setFormDraft(savedForm);
          }
        })
        .catch(() => undefined);
    }

    return () => {
      if (!document.querySelector('.rg-overlay')) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  const handleNavChange = useCallback(
    (next: PublisherNavState) => {
      setNav(next);
      void persistDraft(next, formRef.current);
    },
    [persistDraft]
  );

  const handleFormDraftChange = useCallback(
    (patch: PublisherFormDraft) => {
      setFormDraft((prev) => {
        const next = { ...prev, ...patch };
        void persistDraft(navRef.current, next);
        return next;
      });
    },
    [persistDraft]
  );

  const returnToCreateRequestHub = useCallback(() => {
    void persistDraft();
    consumeContentOpenedFromHub();
    onBack();
  }, [onBack, persistDraft]);

  const handleBack = () => {
    const next = climbPublisherNav(nav);
    if (next) {
      handleNavChange(next);
      return;
    }
    returnToCreateRequestHub();
  };

  const handleClose = () => {
    void persistDraft();
    onClose();
  };

  if (!isOpen) return null;

  const backLabel =
    nav.level === 'home'
      ? '← Вернуться к выбору маршрута'
      : nav.level === 'form'
        ? nav.direction === 'social'
          ? '← Назад'
          : '← К выбору типа'
        : '← К трём направлениям';

  return (
    <div
      className="rg-overlay rg-wizard"
      role="dialog"
      aria-modal="true"
      aria-label="Опубликовать контент"
    >
      <div className="rg-shell rg-wizard-shell">
        <div className="rg-top">
          <div className="rg-top-left">
            <button
              type="button"
              onClick={handleBack}
              className="rg-btn"
              data-rg-back-handler={
                nav.level === 'home' ? 'U153_CONTENT_TO_HUB' : 'PUBLISHER_STACK'
              }
              data-u153-content-from-hub={
                nav.level === 'home' && isContentOpenedFromHub() ? '1' : undefined
              }
              aria-description={
                nav.level === 'home'
                  ? `Return to Create Request Hub (${U153_CONTENT_FROM_HUB})`
                  : undefined
              }
            >
              {backLabel}
            </button>
            <div>
              <small>PUBLISHER 1.81</small>
              <h2>Опубликовать контент</h2>
              <p>Лента ACADEMIA, публичный каталог и передача в соцсети / сайт ENU</p>
            </div>
          </div>
          <button type="button" onClick={handleClose} className="rg-btn">
            Закрыть ×
          </button>
        </div>

        <div className="rg-wizard-body">
          <div className="rg-publisher-surface">
            <PublisherHub
              nav={nav}
              onNavChange={handleNavChange}
              formDraft={formDraft}
              onFormDraftChange={handleFormDraftChange}
              embeddedInGateway
            />
          </div>
        </div>
      </div>
    </div>
  );
};
